/**
 * Minimal unit shape needed by the public-side unit-actions menu, the
 * floor-plan modal and the floor-plan compare modal.
 *
 * Mirrors `ComparableUnit` from the agent (`super-picker/types.ts`) but
 * intentionally keeps **only** the fields the public Available Units /
 * Best PX / Best PSF tables can populate without leaking sensitive data —
 * no raw numeric prices, no DB ids.
 */
export interface PublicUnitForMenu {
  /** Stable key used by the compare cart (uses tower-view `key` when known). */
  unitKey: string;
  /**
   * Project this unit belongs to. Required by the per-row "Reveal prices"
   * flow on multi-project listings (Project Research) so the reveal call
   * can target the correct project's tower-view. Optional because legacy
   * call sites on the Project Detail page don't always populate it (the
   * page's own `projectId` is the source of truth there).
   */
  projectId?: string | null;
  projectName: string;
  unitNumber: string;
  blockNumber: string | null;
  bedroom: number | null;
  bathroom: number | null;
  areaSqft: number | null;
  unitStatus: string | null;
  floorplan: string | null;
  /** Raw FloorNumber from ProjectUnit when known (e.g. "G", "01"). */
  floorNumber?: string | null;
  /** Pre-masked / pre-formatted display strings ("$1,XXX,XXX") — display only. */
  priceDisplay: string | null;
  psfDisplay: string | null;
}

/**
 * Build the synthetic `block-unitNumber` key the public payloads use to
 * identify a tower-view unit without exposing its DB id. Mirrors the
 * backend's `buildSyntheticKey` in `routes/superPickerPublic.ts` exactly,
 * so passing this key to `POST /share/:uuid/reveal` matches the row the
 * masked payload referred to.
 */
export const buildPublicUnitSyntheticKey = (
  blockNumber: string | null | undefined,
  unitNumber: string | null | undefined,
): string => {
  const block = (blockNumber ?? '').trim() || 'B';
  const number = (unitNumber ?? '').trim();
  return `${block.replace(/\s+/g, '')}-${number.replace(/\s+/g, '')}`;
};
